<template>
  <div
    class="dropdown form-basic-dropdown"
    v-on="{
      'show.bs.dropdown': onShow,
      'shown.bs.dropdown': onShown,
      'hide.bs.dropdown': () => (open = false),
    }"
  >
    <a
      href="javascript:void(0);"
      class="btn btn-white btn-md d-inline-flex align-items-center w-100"
      :class="{ disabled }"
      data-bs-toggle="dropdown"
      :data-bs-auto-close="multiple ? 'outside' : 'true'"
      data-bs-popper-config='{"strategy":"fixed"}'
      aria-expanded="false"
    >
      <span class="text-truncate">{{ label }}</span>
      <i
        v-if="clearable && hasValue"
        class="ti ti-x ms-auto flex-shrink-0 clear-icon"
        :title="$t('clear')"
        @click.stop.prevent="clear"
      ></i>
      <i
        class="ti flex-shrink-0"
        :class="[open ? 'ti-chevron-up' : 'ti-chevron-down', { 'ms-auto': !(clearable && hasValue) }]"
      ></i>
    </a>
    <ul ref="menu" class="dropdown-menu dropdown-menu-end p-3 form-dropdown-menu">
      <li v-if="isSearchable" class="mb-2">
        <input
          ref="search"
          v-model="search"
          type="text"
          class="form-control form-control-sm"
          :placeholder="$t('search')"
          @keydown.enter.prevent="chooseFirst"
        />
      </li>
      <li v-for="option in filteredOptions" :key="option.id">
        <a
          href="javascript:void(0);"
          class="dropdown-item rounded-1 d-flex align-items-center justify-content-between"
          :class="{ active: isSelected(option) }"
          @click.prevent="choose(option)"
        >
          <span>{{ option.text }}</span>
          <i v-if="multiple && isSelected(option)" class="ti ti-check ms-2"></i>
        </a>
      </li>
      <li v-if="!filteredOptions.length">
        <span class="dropdown-item-text text-muted">{{ $t('no_data_found') }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'FormDropdown',

  props: {
    // A single id, or an array of ids when `multiple` is set.
    modelValue: { type: null, default: null },
    // [{ id, text, ...extra }]; the whole option is passed to `select`.
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    clearable: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    // Search box appears on its own once the list is long enough to need it.
    searchable: { type: Boolean, default: null },
    disabled: { type: Boolean, default: false },
  },

  emits: ['update:modelValue', 'change', 'select'],

  data() {
    return { open: false, search: '' };
  },

  computed: {
    selectedIds() {
      const value = this.modelValue;
      if (this.multiple) return (Array.isArray(value) ? value : []).map(String);
      return value === null || value === undefined || value === '' ? [] : [String(value)];
    },

    selectedOptions() {
      return this.options.filter(this.isSelected);
    },

    hasValue() {
      return this.selectedIds.length > 0;
    },

    label() {
      const texts = this.selectedOptions.map((o) => o.text);
      return texts.length ? texts.join(', ') : (this.placeholder || this.$t('select_an_option'));
    },

    isSearchable() {
      return this.searchable ?? this.options.length > 8;
    },

    filteredOptions() {
      const term = this.search.trim().toLowerCase();
      if (!term) return this.options;
      return this.options.filter((o) => String(o.text).toLowerCase().includes(term));
    },
  },

  methods: {
    isSelected(option) {
      return this.selectedIds.includes(String(option.id));
    },

    emitValue(value) {
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },

    choose(option) {
      if (this.multiple) {
        const current = Array.isArray(this.modelValue) ? this.modelValue : [];
        const next = this.isSelected(option)
          ? current.filter((id) => String(id) !== String(option.id))
          : [...current, option.id];
        this.emitValue(next);
      } else {
        this.emitValue(option.id);
        this.search = '';
      }
      this.$emit('select', option);
    },

    clear() {
      this.emitValue(this.multiple ? [] : null);
    },

    chooseFirst() {
      if (!this.filteredOptions.length) return;
      this.choose(this.filteredOptions[0]);
      if (!this.multiple) {
        const toggle = this.$el.querySelector('[data-bs-toggle="dropdown"]');
        window.bootstrap?.Dropdown.getInstance(toggle)?.hide();
      }
    },

    onShow() {
      // The menu is fixed-positioned (so tables/modals don't clip it), so width can't be 100%.
      this.$refs.menu.style.width = `${this.$el.offsetWidth}px`;
    },

    onShown() {
      this.open = true;
      if (this.isSearchable) this.$nextTick(() => this.$refs.search?.focus());
    },
  },
};
</script>

<style scoped>
.form-basic-dropdown > .btn,
.form-basic-dropdown > .btn:hover,
.form-basic-dropdown > .btn:focus,
.form-basic-dropdown > .btn.show {
  color: #000;
}

.form-basic-dropdown > .btn {
  gap: 0.5rem;
  min-height: 36px;
}

.form-dropdown-menu .dropdown-item:not(.active):not(:hover) {
  color: #000;
}

.form-dropdown-menu .dropdown-item.active,
.form-dropdown-menu .dropdown-item.active .ti {
  color: #000;
}

.form-basic-dropdown > .btn .ti {
  color: #000;
}

.form-basic-dropdown > .btn .clear-icon {
  color: #6c757d;
  font-size: 0.85rem;
}

.form-dropdown-menu {
  min-width: 160px;
  max-height: 280px;
  overflow-y: auto;
}
</style>
